'use client';

import { Check, X, Loader2, ArrowRight, Clock } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Link } from '@/i18n/routing';
import styles from './AuditPricing.module.css';

/**
 * Pricing section, structured as two deliberate blocks:
 *   1. Self-serve  -> Free Scan + Full Report (the platform reveals/sizes the pain)
 *
 * Both self-serve CTAs enter through the free scan. The Full Report is an upgrade
 * OF a specific report, so it cannot be sold before a URL exists: a checkout with
 * no report behind it produced a paid customer with nothing to deliver on
 * 2026-06-26. It also converted worse, asking $99 with no evidence shown instead
 * of asking it next to "N issues found, X locked".
 *   2. Work with an expert -> Expert Review + Growth (the bridge toward the retainer)
 *
 * The retainer itself lives off the table, in conversation. Nothing here crosses
 * into "we fix it for you" — diagnosis on the platform, execution in the retainer.
 */
export function AuditPricing() {
  const t = useTranslations('audit.pricing');
  const locale = useLocale();
  const [showGrowthModal, setShowGrowthModal] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const openGrowthModal = () => {
    setError('');
    setEmail('');
    setWaitlistDone(false);
    setShowGrowthModal(true);
  };

  const handleGrowthWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/growth/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale }),
      });
      const data = await response.json();
      if (data.success) {
        setWaitlistDone(true);
      } else {
        setError(data.error || 'Could not join the waitlist. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderFeature = (text: string) => (
    <li className={styles.feature}>
      <Check className={styles.featureIcon} size={16} strokeWidth={2.5} />
      <span>{text}</span>
    </li>
  );

  return (
    <section className={styles.section} id="pricing">
      <div className="audit-container">
        <div className={styles.header}>
          <span className={styles.label}>{t('label')}</span>
          <h2 className={styles.title}>{t('title')}</h2>
          <p className={styles.subtitle}>{t('subtitle')}</p>
          <Link href="/criteria" className={styles.frameworkLink}>
            {t('frameworkLink')}
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* Block 1: Self-serve */}
        <div className={styles.bandHeader}>
          <span className={styles.label}>{t('blocks.selfServe.label')}</span>
          <h3 className={styles.bandTitle}>{t('blocks.selfServe.title')}</h3>
        </div>

        <div className={styles.grid}>
          {/* Free Scan */}
          <div className={styles.card}>
            <h3 className={styles.cardName}>{t('tiers.free.name')}</h3>
            <p className={styles.cardDesc}>{t('tiers.free.description')}</p>
            <div className={styles.billingStatic}>
              <span className={styles.staticPill}>{t('tiers.free.billing')}</span>
            </div>
            <div className={styles.priceSection}>
              <div className={styles.priceRow}>
                <span className={styles.price}>{t('tiers.free.price')}</span>
                <span className={styles.period}>{t('tiers.free.period')}</span>
              </div>
            </div>
            <ul className={styles.features}>
              {renderFeature(t('features.free.topIssues'))}
              {renderFeature(t('features.free.desktop'))}
              {renderFeature(t('features.free.noSignup'))}
              {renderFeature(t('features.free.instant'))}
            </ul>
            <Link href="/audit" className={styles.cardLinkButton}>
              {t('tiers.free.cta')}
            </Link>
          </div>

          {/* Full Report */}
          <div className={styles.card}>
            <h3 className={styles.cardName}>{t('tiers.payAsYouGo.name')}</h3>
            <p className={styles.cardDesc}>{t('tiers.payAsYouGo.description')}</p>
            <div className={styles.billingStatic}>
              <span className={styles.staticPill}>{t('tiers.payAsYouGo.billing')}</span>
            </div>
            <div className={styles.priceSection}>
              <div className={styles.priceRow}>
                <span className={styles.price}>{t('tiers.payAsYouGo.price')}</span>
                <span className={styles.period}>{t('tiers.payAsYouGo.period')}</span>
              </div>
            </div>
            <ul className={styles.features}>
              {renderFeature(t('features.payAsYouGo.fullAnalysis'))}
              {renderFeature(t('features.payAsYouGo.oneReport'))}
              {renderFeature(t('features.payAsYouGo.noSubscription'))}
              {renderFeature(t('features.payAsYouGo.pdfDownload'))}
            </ul>
            <Link href="/audit" className={styles.cardLinkButton}>
              {t('tiers.payAsYouGo.cta')}
            </Link>
            <p className={styles.upgradeNote}>{t('tiers.payAsYouGo.upgradeNote')}</p>
            <Link href="/demo-report" className={styles.sampleLink}>
              {t('viewSample')}
            </Link>
          </div>
        </div>

        {/* Block 2: Work with an expert */}
        <div className={styles.bandHeader}>
          <span className={styles.label}>{t('blocks.expert.label')}</span>
          <h3 className={styles.bandTitle}>{t('blocks.expert.title')}</h3>
        </div>

        <div className={styles.expertSolo}>
          {/* Expert Review: the single, focused bridge to the retainer */}
          <div className={`${styles.card} ${styles.cardHighlight}`}>
            <h3 className={styles.cardName}>{t('tiers.expert.name')}</h3>
            <p className={styles.cardDesc}>{t('tiers.expert.description')}</p>
            <div className={styles.billingStatic}>
              <span className={styles.staticPill}>{t('tiers.expert.billing')}</span>
            </div>
            <div className={styles.priceSection}>
              <div className={styles.priceRow}>
                <span className={styles.price}>{t('tiers.expert.price')}</span>
                <span className={styles.period}>{t('tiers.expert.period')}</span>
              </div>
            </div>
            <ul className={styles.features}>
              {renderFeature(t('features.expert.reportIncluded'))}
              {renderFeature(t('features.expert.session'))}
              {renderFeature(t('features.expert.walkthrough'))}
              {renderFeature(t('features.expert.recording'))}
            </ul>
            <p className={styles.feeCredit}>{t('tiers.expert.feeCredit')}</p>
            <Link href="/discovery-call" className={styles.cardLinkButton}>
              {t('tiers.expert.cta')}
            </Link>
          </div>
        </div>

        {/* Growth: demoted from a priced card to one discreet coming-soon line.
            Waitlist modal + /api/growth/waitlist capture are unchanged. */}
        <p className={styles.growthNote}>
          {t('growthNote')}{' '}
          <button type="button" className={styles.growthNoteLink} onClick={openGrowthModal}>
            {t('tiers.growth.cta')}
          </button>
        </p>

        {/* Retainer anchor: the real destination, sold in conversation (no checkout) */}
        <p className={styles.retainerAnchor}>{t('retainerAnchor')}</p>

        {/* Retainer vs hiring comparison (separate from the audit ValueComparison) */}
        <div className={styles.retainerCompare}>
          <div className={styles.bandHeader}>
            <span className={styles.label}>{t('retainerComparison.label')}</span>
            <h3 className={styles.bandTitle}>{t('retainerComparison.title')}</h3>
          </div>
          <div className={styles.compareGrid}>
            <div className={styles.compareCol}>
              <h4 className={styles.compareName}>{t('retainerComparison.inHouse.name')}</h4>
              <ul className={styles.compareList}>
                <li>{t('retainerComparison.inHouse.p1')}</li>
                <li>{t('retainerComparison.inHouse.p2')}</li>
                <li>{t('retainerComparison.inHouse.p3')}</li>
                <li>{t('retainerComparison.inHouse.p4')}</li>
              </ul>
            </div>
            <div className={styles.compareCol}>
              <h4 className={styles.compareName}>{t('retainerComparison.agency.name')}</h4>
              <ul className={styles.compareList}>
                <li>{t('retainerComparison.agency.p1')}</li>
                <li>{t('retainerComparison.agency.p2')}</li>
                <li>{t('retainerComparison.agency.p3')}</li>
              </ul>
            </div>
            <div className={`${styles.compareCol} ${styles.compareColOurs}`}>
              <h4 className={styles.compareName}>{t('retainerComparison.ours.name')}</h4>
              <ul className={styles.compareList}>
                <li>{t('retainerComparison.ours.p1')}</li>
                <li>{t('retainerComparison.ours.p2')}</li>
                <li>{t('retainerComparison.ours.p3')}</li>
                <li>{t('retainerComparison.ours.p4')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Discreet custom / contact line (no Enterprise tier) */}
        <p className={styles.bridge}>
          {t('customCta.text')}{' '}
          <a href="mailto:hello@interfaceaudit.com?subject=Custom%20engagement" className={styles.bridgeLink}>
            {t('customCta.cta')}
          </a>
        </p>
      </div>

      {/* Growth waitlist modal (capture only, no checkout) */}
      {showGrowthModal && (
        <div className={styles.modalOverlay} onClick={() => setShowGrowthModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setShowGrowthModal(false)} aria-label="Close">
              <X size={20} />
            </button>
            {waitlistDone ? (
              <div className={styles.modalHeader}>
                <Check size={24} className={styles.modalIcon} />
                <h3 className={styles.modalTitle}>{t('modal.growth.successTitle')}</h3>
                <p className={styles.modalSubtitle}>{t('modal.growth.successBody')}</p>
              </div>
            ) : (
              <>
                <div className={styles.modalHeader}>
                  <Clock size={24} className={styles.modalIcon} />
                  <h3 className={styles.modalTitle}>{t('modal.growth.title')}</h3>
                  <p className={styles.modalSubtitle}>{t('modal.growth.subtitle')}</p>
                </div>
                <form onSubmit={handleGrowthWaitlist} className={styles.modalForm}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('modal.emailPlaceholder')}
                    className={styles.modalInput}
                    required
                    autoFocus
                  />
                  {error && <p className={styles.modalError}>{error}</p>}
                  <Button type="submit" variant="primary" size="md" style={{ width: '100%' }} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className={styles.spinner} />
                        {t('modal.processing')}
                      </>
                    ) : (
                      t('modal.growth.cta')
                    )}
                  </Button>
                  <p className={styles.modalTerms}>{t('modal.growth.terms')}</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
